/**
 * Audio Service
 *
 * Client-side API for managing slide audio:
 * - Script management
 * - Audio generation
 * - Voice settings
 */

import axios from 'axios';
import { authorizeRequest } from './authHeaders';

// Types
export type AudioStatus = 'PENDING' | 'GENERATING' | 'ALIGNING' | 'COMPLETED' | 'FAILED';

export interface WordTiming {
  word: string;
  startTime: number;
  endTime: number;
  confidence: number;
}

export interface SlideAudioVersion {
  id: string;
  slideAudioId: string;
  version: number;
  scriptText: string;
  scriptSource: 'user' | 'ai';
  voiceId: string;
  voiceName: string;
  provider: string;
  languageCode: string;
  ttsModel: string | null;
  audioUrl: string | null;
  audioDuration: number | null;
  wordTimings: WordTiming[] | null;
  status: AudioStatus;
  generationJobId: string | null;
  errorMessage: string | null;
  isCurrentVersion: boolean;
  createdBy: string;
  createdAt: string;
}

export interface ConversationAudioSettings {
  voiceId: string;
  voiceName: string;
  provider: string;
  languageCode: string;
  ttsModel: string;
}

export interface TTSVoice {
  id: string;
  name: string;
  description: string;
  gender: 'male' | 'female';
  provider: string;
  sampleUrl: string;  // The voice on gemini-3.8-flash-tts, every voice saying the same line
  persona: string;    // Google's persona, e.g. "News & Podcast Host"
  summary: string;    // Google's one-line description
}

export interface SlideAudioStatusSummary {
  slideId: string;
  slideIdentifier: string;
  slideNumber: number;
  title: string | null;
  hasAudio: boolean;
  audioStatus: AudioStatus | null;
  audioUrl: string | null;
  audioDuration: number | null;
  version: number | null;
  scriptText: string | null;
}

export interface SlidePromptsResponse {
  slideId: string;
  // Single active prompt (preferred)
  activePrompt: string | null;
  // Legacy fields for backward compatibility
  promptFlash: string | null;
  promptPro: string | null;
  preferredModel: string;  // @deprecated
  isVideoLevel: boolean;  // True if prompt is from video-level narrativeContext
}

// Applied audio version info - tracks which audio version is applied to the slide
export interface AppliedVersionInfo {
  id: string;
  version: number;
  voiceName: string;
  voiceId: string;
  audioDuration: number | null;
  appliedAt: string | null;
}

// Extended audio response that includes applied version tracking
export interface SlideAudioResponse extends SlideAudioVersion {
  appliedVersion: AppliedVersionInfo | null;
}

export interface UpdateSlidePromptsInput {
  // Single prompt (preferred)
  prompt?: string;
  // Legacy fields for backward compatibility
  promptFlash?: string;
  promptPro?: string;
  preferredModel?: 'flash' | 'pro';  // @deprecated
}

// Create axios instance for audio API
const audioApi = axios.create({
  timeout: 300000, // 5 minutes for audio generation
  headers: {
    'Content-Type': 'application/json',
  },
});

audioApi.interceptors.request.use(authorizeRequest, (error) => Promise.reject(error));

// ============================================
// Slide Audio API
// ============================================

/**
 * Get current audio for a slide
 * Returns audio version with applied version tracking
 */
export async function getSlideAudio(slideId: string): Promise<SlideAudioResponse | null> {
  const response = await audioApi.get(`/slides/${slideId}/audio`);
  if (response.data.exists === false) {
    return null;
  }
  return response.data;
}

/**
 * List all audio versions for a slide
 */
export async function listAudioVersions(slideId: string, options: { signal?: AbortSignal } = {}): Promise<SlideAudioVersion[]> {
  const response = await audioApi.get(`/slides/${slideId}/audio/versions`, { signal: options.signal });
  return response.data;
}

/**
 * Create new audio version with script
 */
export async function createAudioVersion(
  slideId: string,
  scriptText: string,
  options?: {
    voiceId?: string;
    voiceName?: string;
    generateImmediately?: boolean;
  }
): Promise<SlideAudioVersion & { generationJobId?: string; generationQueued?: boolean }> {
  const response = await audioApi.post(`/slides/${slideId}/audio`, {
    scriptText,
    ...options,
  });
  return response.data;
}

/**
 * Generate audio for current script version
 * @param slideId - The slide ID
 * @param options - Optional voice/model overrides
 */
export async function generateAudio(
  slideId: string,
  options?: {
    voiceId?: string;
    voiceName?: string;
    ttsModel?: 'flash' | 'pro';
  }
): Promise<{
  jobId: string;
  status: string;
}> {
  const response = await audioApi.post(`/slides/${slideId}/audio/generate`, options || {});
  return response.data;
}

/**
 * Update script text (creates new version)
 */
export async function updateScript(
  slideId: string,
  scriptText: string,
  voiceConfig?: { voiceId: string; voiceName: string }
): Promise<SlideAudioVersion> {
  const response = await audioApi.put(`/slides/${slideId}/audio/script`, {
    scriptText,
    ...voiceConfig,
  });
  return response.data;
}

/**
 * Switch to a different audio version
 */
export async function switchAudioVersion(
  slideId: string,
  version: number
): Promise<SlideAudioVersion> {
  const response = await audioApi.post(`/slides/${slideId}/audio/switch/${version}`);
  return response.data;
}

// ============================================
// Apply Audio to Slide API
// ============================================

export interface ApplyAudioResult {
  success: boolean;
  slideId: string;
  appliedVersionId: string;
  appliedVersion: number;
  audioConfigPath: string;
  syncAnimations: boolean;
  message: string;
}

/**
 * Apply audio to slide - writes audio config to source/audio.json
 * Optionally syncs animations using choreographer agent (future feature)
 *
 * @param slideId - The slide ID
 * @param options - Apply options
 *   - versionId: (optional) specific audio version ID, defaults to current
 *   - syncAnimations: if true, choreographer will sync animations to audio timings
 */
export async function applyAudioToSlide(
  slideId: string,
  options: {
    versionId?: string;
    syncAnimations: boolean;
  }
): Promise<ApplyAudioResult> {
  const response = await audioApi.post(`/slides/${slideId}/audio/apply`, options);
  return response.data;
}

// ============================================
// Slide TTS Prompts API
// ============================================

/**
 * Get TTS prompts for a slide
 */
export async function getSlidePrompts(slideId: string): Promise<SlidePromptsResponse> {
  const response = await audioApi.get(`/slides/${slideId}/audio/prompts`);
  return response.data;
}

/**
 * Update TTS prompts for a slide
 */
export async function updateSlidePrompts(
  slideId: string,
  prompts: UpdateSlidePromptsInput
): Promise<SlidePromptsResponse> {
  const response = await audioApi.put(`/slides/${slideId}/audio/prompts`, prompts);
  return response.data;
}

// ============================================
// Conversation Audio Settings API
// ============================================

/**
 * Get conversation audio settings
 */
export async function getAudioSettings(
  conversationId: string
): Promise<ConversationAudioSettings> {
  const response = await audioApi.get(`/conversations/${conversationId}/audio/settings`);
  return response.data;
}

/**
 * Update conversation audio settings
 */
export async function updateAudioSettings(
  conversationId: string,
  settings: Partial<ConversationAudioSettings>
): Promise<ConversationAudioSettings> {
  const response = await audioApi.put(`/conversations/${conversationId}/audio/settings`, settings);
  return response.data;
}

/**
 * Generate audio for all slides in conversation
 */
export async function generateAllAudio(conversationId: string): Promise<{
  jobIds: string[];
  count: number;
  status: string;
}> {
  const response = await audioApi.post(`/conversations/${conversationId}/audio/generate-all`);
  return response.data;
}

/**
 * Deterministically apply a user-corrected audio seam split (no chat orchestrator).
 * The worker re-slices via the same splitMasterAudioCore the agent uses, then the
 * storyboard refreshes off the outline-updated SSE event. Returns the queued jobId.
 */
export async function queueAudioSplit(
  conversationId: string,
  audioSplitLocks: Array<{ outlineSlideId: string; cutTime: number }>,
): Promise<{ jobId: string; status: string }> {
  const response = await audioApi.post(`/conversations/${conversationId}/audio/split`, { audioSplitLocks });
  return response.data;
}

/**
 * Get audio status for all slides in conversation
 */
export async function getConversationAudioStatus(conversationId: string): Promise<{
  slides: SlideAudioStatusSummary[];
  summary: {
    totalSlides: number;
    withAudio: number;
    pending: number;
    generating: number;
    failed: number;
    noScript: number;
  };
}> {
  const response = await audioApi.get(`/conversations/${conversationId}/audio/status`);
  return response.data;
}

// ============================================
// Voice Catalog API
// ============================================

/**
 * Get available TTS voices
 */
export async function getAvailableVoices(): Promise<{
  voices: TTSVoice[];
  defaultVoice: string;
  providers: string[];
}> {
  const response = await audioApi.get('/audio/voices');
  return response.data;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Format audio duration as MM:SS
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get status display info
 */
export function getStatusInfo(status: AudioStatus | null): {
  label: string;
  color: string;
  isLoading: boolean;
} {
  switch (status) {
    case 'PENDING':
      return { label: 'Script Ready', color: 'text-yellow-500', isLoading: false };
    case 'GENERATING':
      return { label: 'Generating Audio...', color: 'text-primary', isLoading: true };
    case 'ALIGNING':
      return { label: 'Processing Timings...', color: 'text-primary', isLoading: true };
    case 'COMPLETED':
      return { label: 'Audio Ready', color: 'text-green-500', isLoading: false };
    case 'FAILED':
      return { label: 'Generation Failed', color: 'text-red-500', isLoading: false };
    default:
      return { label: 'No Script', color: 'text-gray-400', isLoading: false };
  }
}

// ============================================
// Apply Audio with Choreography (Shared Function)
// ============================================

/**
 * Parameters for applyAudioWithChoreography
 */
export interface ApplyAudioWithChoreographyParams {
  /** The database slide ID (UUID) */
  slideId: string;
  /** The outline slide identifier (e.g., "slide-001") used for file paths */
  slideIdentifier: string;
  /** The parent conversation ID */
  conversationId: string;
  /** Optional specific audio version ID to apply (defaults to current version) */
  versionId?: string;
  /** The narration script text (used by choreographer for context) */
  scriptText?: string;
  /** Callback to register the choreography job with useApplyJobStatus hook */
  registerJob: (slideId: string, jobId: string) => void;
}

/**
 * Result of applyAudioWithChoreography
 */
export interface ApplyAudioWithChoreographyResult {
  /** Whether the operation succeeded */
  success: boolean;
  /** Whether a choreography job was created (false if no word timings) */
  choreographyJobCreated: boolean;
  /** Error message if success is false */
  error?: string;
}

/**
 * Apply audio to a slide with optional animation choreography.
 * This is the single source of truth for audio application.
 *
 * Flow:
 * 1. Fetch audio data to check for word timings
 * 2. Apply audio configuration via REST API
 * 3. If word timings exist, execute animation choreographer agent
 * 4. Register job with useApplyJobStatus hook for SSE tracking
 *
 * @example
 * ```typescript
 * const { registerJob } = useApplyJobStatus(conversationId);
 *
 * const result = await applyAudioWithChoreography({
 *   slideId,
 *   slideIdentifier,
 *   conversationId,
 *   versionId: audioVersion?.id,
 *   scriptText,
 *   registerJob,
 * });
 *
 * if (result.success && result.choreographyJobCreated) {
 *   toast.success('Syncing animations...');
 * }
 * ```
 */
export async function applyAudioWithChoreography(
  params: ApplyAudioWithChoreographyParams
): Promise<ApplyAudioWithChoreographyResult> {
  const { slideId, slideIdentifier, conversationId, versionId, scriptText, registerJob } = params;

  // Validate slideId early - empty string means slide doesn't exist yet
  // This prevents the race condition where multiple slides share the same job
  if (!slideId || slideId.length === 0) {
    console.error(`[applyAudioWithChoreography] Invalid slideId for ${slideIdentifier} - slide may not exist yet`);
    return { success: false, choreographyJobCreated: false, error: 'Invalid slideId - slide does not exist yet' };
  }

  // Lazy import to avoid circular dependency
  const { executeAgentJob } = await import('./agentJobService');

  try {
    // 1. Fetch audio data to check for word timings
    const audioData = await getSlideAudio(slideId);
    if (!audioData) {
      return { success: false, choreographyJobCreated: false, error: 'No audio data found' };
    }

    const hasWordTimings = !!(audioData.wordTimings && audioData.wordTimings.length > 0);

    // 2. Apply audio configuration via REST API
    const applyResult = await applyAudioToSlide(slideId, {
      versionId: versionId || audioData.id,
      syncAnimations: hasWordTimings,
    });

    if (!applyResult.success) {
      return { success: false, choreographyJobCreated: false, error: 'Failed to apply audio configuration' };
    }

    // 3. If no word timings, we're done (no choreography needed)
    if (!hasWordTimings) {
      console.log(`[applyAudioWithChoreography] No word timings for ${slideIdentifier}, skipping choreography`);
      return { success: true, choreographyJobCreated: false };
    }

    // 4. Execute animation choreographer agent job
    const slideDir = `slides/${slideIdentifier || slideId}`;

    const response = await executeAgentJob({
      conversationId,
      agentName: 'animation_choreographer',
      inputs: {
        slideDirectory: slideDir,
        wordTimings: JSON.stringify(audioData.wordTimings),
        audioDuration: audioData.audioDuration || 0,
        scriptText: scriptText || '',
      },
      metadata: {
        slideId,
        slideIdentifier,
        audioDuration: audioData.audioDuration,
      },
    });

    // 5. Register with hook for SSE tracking
    registerJob(slideId, response.job.id);

    console.log(`[applyAudioWithChoreography] Choreography job created: ${response.job.id} for ${slideIdentifier}`);

    return { success: true, choreographyJobCreated: true };
  } catch (err: any) {
    console.error(`[applyAudioWithChoreography] Failed for ${slideIdentifier}:`, err);
    return {
      success: false,
      choreographyJobCreated: false,
      error: err.message || 'Unknown error',
    };
  }
}
